'use client';

// Next.js route-level error boundary — reports then shows recovery UI.

import { useEffect } from 'react';
import Link from 'next/link';
import Button, { buttonClass } from '@/components/ui/Button';
import {
  EVENTS,
  captureException,
  safeErrorMessage,
  track,
} from '@/lib/analytics';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    const message = safeErrorMessage(error);
    track(EVENTS.client_error, {
      message,
      source: 'error.tsx',
      digest: error.digest,
    });
    captureException(error, { source: 'error.tsx', digest: error.digest });
  }, [error]);

  // The site's own ladder and type roles: this used to hand-roll two bordered
  // pills with an accent hover and two raw sizes, the one place an action
  // spent the accent (AGENTS.md, Button + selection rule).
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[var(--app-bg)] px-6 text-center text-[var(--rt-ink)]">
      <div className="max-w-sm space-y-2">
        <h1 className="text-title font-semibold">Something went wrong</h1>
        <p className="text-body text-[var(--rt-ink-dim)]">
          This page hit an unexpected error. Try again or return home.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Button type="button" variant="secondary" size="md" onClick={reset}>
          Try again
        </Button>
        <Link href="/" className={buttonClass({ variant: 'ghost', size: 'md' })}>
          Back to home
        </Link>
      </div>
    </div>
  );
}
